import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useClerk, useUser } from "@clerk/react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useFeatureFlags } from "../../lib/tenant/useFeatureFlags";
import { useNipeControl } from "../nipe/NipeControlContext";
import { testIds } from "../../lib/testIds";
import { getXpIcon } from "../../lib/desktop/iconMap";
import { START_MENU_SEARCH_EVENT } from "../corpus/CorpusSearchWindow";

const ORIENTATION_FLAG = "repancy-orientation-completed";
// Phase 6 — Start Menu "Take the tour" item dispatches this event to
// remount the first-run tour overlay. The actual handler lives in
// Win95Desktop's FirstRunTourMount (see TOUR_RELAUNCH_EVENT). Defined
// inline rather than imported to avoid a Win95Desktop ↔ StartMenu
// import cycle.
const TOUR_RELAUNCH_EVENT = "repancy:tour-relaunch";

interface StartMenuProps {
  open: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  onItemClick: (path: string) => void;
}

interface MenuRowProps {
  label: string;
  iconPath: string;
  onClick: () => void;
  testId?: string;
  emphasis?: boolean;
  danger?: boolean;
}

function MenuRow({ label, iconPath, onClick, testId, emphasis, danger }: MenuRowProps) {
  // Match XP Start Menu hover: solid blue strip (`#316ac5`) with white
  // text, full-row sweep. Implemented via inline event handlers so we
  // get reliable behavior independent of Tailwind v4 arbitrary-value
  // hover-variant quirks. Text color stays explicit so memory
  // `feedback_no_light_grey_text` is honoured at rest.
  //
  // Single-line label with ellipsis prevents wrap (real XP doesn't wrap
  // Start Menu labels — they truncate or the menu widens).
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={[
        "flex items-center gap-2.5 w-full text-left px-3",
        "cursor-pointer select-none",
        emphasis ? "font-bold" : "font-normal",
      ].join(" ")}
      style={{
        fontFamily: "var(--xp-font-body)",
        fontSize: 13,
        background: "transparent",
        border: "none",
        color: danger ? "#7a0a0a" : "#000",
        transition: "background-color 80ms",
        height: 30,
        whiteSpace: "nowrap",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        const b = e.currentTarget;
        b.style.background = "var(--xp-selection)";
        b.style.color = "#fff";
      }}
      onMouseLeave={(e) => {
        const b = e.currentTarget;
        b.style.background = "transparent";
        b.style.color = danger ? "#7a0a0a" : "#000";
      }}
    >
      <img
        src={iconPath}
        alt=""
        draggable={false}
        style={{
          width: 24,
          height: 24,
          imageRendering: iconPath.endsWith(".svg") ? "auto" : "pixelated",
          flexShrink: 0,
        }}
      />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
          flex: 1,
        }}
      >
        {label}
      </span>
    </button>
  );
}

interface SubmenuItem {
  label: string;
  iconPath: string;
  onClick: () => void;
  testId?: string;
}

interface SubmenuRowProps {
  label: string;
  iconPath: string;
  items: ReadonlyArray<SubmenuItem>;
  testId?: string;
}

/**
 * XP-style cascading submenu — hover the parent row, the child panel
 * slides out to the right after a short delay. Click anywhere on the
 * parent (or a child) to act. Convention matches the XP Programs ▶
 * pattern: a small right-pointing chevron + hover-only open.
 */
function SubmenuRow({ label, iconPath, items, testId }: SubmenuRowProps) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 w-full text-left px-3 cursor-pointer select-none"
        style={{
          fontFamily: "var(--xp-font-body)",
          fontSize: 13,
          background: open ? "var(--xp-selection)" : "transparent",
          color: open ? "#fff" : "#000",
          border: "none",
          height: 30,
          whiteSpace: "nowrap",
          overflow: "hidden",
          transition: "background-color 80ms",
        }}
      >
        <img
          src={iconPath}
          alt=""
          draggable={false}
          style={{
            width: 24,
            height: 24,
            imageRendering: iconPath.endsWith(".svg") ? "auto" : "pixelated",
            flexShrink: 0,
          }}
        />
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </span>
        <span aria-hidden style={{ marginLeft: 8, fontSize: 10 }}>▶</span>
      </button>
      {open && (
        <div
          role="menu"
          data-testid={testId ? `${testId}-submenu` : undefined}
          style={{
            position: "absolute",
            top: 0,
            left: "100%",
            marginLeft: 2,
            background: "#fff",
            border: "1px solid #98c4f5",
            boxShadow: "2px 2px 4px rgba(0,0,0,0.25)",
            minWidth: 220,
            paddingTop: 4,
            paddingBottom: 4,
            zIndex: 5,
          }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {items.map((it) => (
            <MenuRow
              key={it.label}
              label={it.label}
              iconPath={it.iconPath}
              testId={it.testId}
              onClick={() => { setOpen(false); it.onClick(); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * XP-style Search row for the Start Menu right column. Click the row
 * label to open Corpus Search empty; type into the inline input + press
 * Enter to open it with the query pre-seeded.
 *
 * The submit handler is hoisted up so the parent owns the
 * onItemClick / onClose / event-dispatch wiring.
 */
function StartMenuSearchRow({ onSubmit }: { onSubmit: (query: string) => void }) {
  const [query, setQuery] = useState("");
  return (
    <div
      // Mobile: drop horizontal padding to a single side, shorten gap, give
      // the input room to actually be typeable. The desktop layout is
      // unchanged above sm: (24px icon, gap-2.5, px-3).
      className="flex items-center w-full gap-1 px-1.5 sm:gap-2.5 sm:px-3"
      style={{
        fontFamily: "var(--xp-font-body)",
        fontSize: 13,
        color: "#000",
        height: 30,
      }}
    >
      {/* Hide the magnifying-glass icon on mobile — it eats 24px + gap that
          the input desperately needs in a 160px column. Visible on sm+. */}
      <img
        src={getXpIcon("/corpus-search")}
        alt=""
        draggable={false}
        className="hidden sm:block"
        style={{
          width: 24,
          height: 24,
          imageRendering: "pixelated",
          flexShrink: 0,
        }}
      />
      <form
        className="flex flex-1 items-center gap-1 min-w-0"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(query);
          setQuery("");
        }}
      >
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          data-testid={testIds.startMenu.item("Search")}
          aria-label="Search the firm corpus"
          // h-7 (28px) on mobile = touch-friendly; reverts to inline 20px
          // via sm: utility. min-w-0 + flex-1 lets the input shrink+grow.
          className="flex-1 min-w-0 h-7 sm:h-5"
          style={{
            background: "#fff",
            border: "1px solid #7f9db9",
            padding: "1px 6px",
            fontFamily: "var(--xp-font-body)",
            fontSize: 12,
            color: "#000",
            width: 0,
            minWidth: 0,
          }}
        />
        <button
          type="submit"
          aria-label="Run search"
          title="Search"
          data-xp-primary
          // h-7 (28px) on mobile so the touch target matches the input.
          // Desktop stays at 20px.
          className="h-7 sm:h-5"
          style={{
            padding: "1px 10px",
            fontFamily: "var(--xp-font-body)",
            fontSize: 11,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          Go
        </button>
      </form>
    </div>
  );
}

export function StartMenu({ open, onClose, buttonRef, onItemClick }: StartMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { signOut } = useClerk();
  const { user } = useUser();
  const nipe = useNipeControl();
  const flags = useFeatureFlags();

  function relaunchOrientation(): void {
    // Re-fire the first-run spotlight tour. Clear both the legacy
    // orientation flag and the new tour-completed flag so the tour
    // re-fires regardless of which one is set.
    try {
      window.localStorage.removeItem(ORIENTATION_FLAG);
      window.localStorage.removeItem("repancy:tour-completed");
    } catch {
      // localStorage unavailable
    }
    onClose();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(TOUR_RELAUNCH_EVENT));
    }
  }

  useEffect(() => {
    if (!open) return;

    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current && !menuRef.current.contains(target)) {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleMouseDown, true);
    return () => document.removeEventListener("mousedown", handleMouseDown, true);
  }, [open, onClose, buttonRef]);

  const displayName = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? "Repancy User";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={menuRef}
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 8, opacity: 0 }}
          transition={{ duration: 0.1 }}
          data-testid={testIds.startMenu.root}
          className="absolute bottom-full left-0 z-[1100]"
          style={{
            // Wider menu so two-word labels ("Document Library", "Show
            // me around again", "Create my private workspace") don't
            // wrap on the left column. Real XP Start Menu was ~360px
            // per column — we run a touch narrower per column but no
            // wrap thanks to ellipsis on the MenuRow span.
            width: isMobile ? 320 : 520,
            background: "#fff",
            border: "2px solid #245edb",
            borderRadius: "8px 8px 0 0",
            boxShadow: "2px 2px 8px rgba(0,0,0,0.5)",
            overflow: "hidden",
            fontFamily: "var(--xp-font-body)",
          }}
        >
          {/* Header — blue gradient with user name (XP "logged-in user" strip) */}
          <div
            className="flex items-center gap-3 px-3 py-2"
            style={{
              background:
                "linear-gradient(180deg, var(--xp-titlebar-from) 0%, var(--xp-titlebar-to) 100%)",
              color: "#fff",
              borderBottom: "2px solid #245edb",
            }}
          >
            <div
              className="flex items-center justify-center bg-white"
              style={{
                width: 38,
                height: 38,
                borderRadius: 4,
                border: "2px solid #fff",
              }}
            >
              <img
                src={getXpIcon("/profile")}
                alt=""
                style={{ width: 30, height: 30, imageRendering: "pixelated" }}
              />
            </div>
            <span
              style={{
                fontFamily: "var(--xp-font-display)",
                fontWeight: 700,
                fontSize: 15,
                textShadow: "1px 1px 1px rgba(0,0,0,0.4)",
              }}
            >
              {displayName}
            </span>
          </div>

          {/* Two-column body */}
          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {/* Left column — frequently-used apps */}
            <div className="py-1.5 pr-1" style={{ background: "#fff" }}>
              <MenuRow
                label="Document Library"
                iconPath={getXpIcon("/documents")}
                emphasis
                testId={testIds.startMenu.item("Document Library")}
                onClick={() => {
                  onItemClick("/documents");
                  onClose();
                }}
              />
              <MenuRow
                label="Prompt Lab"
                iconPath={getXpIcon("/prompt-lab")}
                emphasis
                testId={testIds.startMenu.item("Prompt Lab")}
                onClick={() => {
                  onItemClick("/prompt-lab");
                  onClose();
                }}
              />
              {/* Client List moved further down (after Notes) so the
                  top of the Start menu surfaces the lawyer's primary
                  workbench surfaces. */}
              <MenuRow
                label="Skill Library"
                iconPath={getXpIcon("/skills")}
                testId={testIds.startMenu.item("Skill Library")}
                onClick={() => {
                  onItemClick("/skills");
                  onClose();
                }}
              />
              <MenuRow
                label="Specialization Lab"
                iconPath={getXpIcon("/specialization-lab")}
                testId={testIds.startMenu.item("Specialization Lab")}
                onClick={() => {
                  onItemClick("/specialization-lab");
                  onClose();
                }}
              />
              {flags.nipeEnabled ? (
                <MenuRow
                  label="NIPE"
                  iconPath={getXpIcon("/help")}
                  testId={testIds.startMenu.item("NIPE")}
                  onClick={() => {
                    nipe.open();
                    onClose();
                  }}
                />
              ) : null}
              <MenuRow
                label="Take the tour"
                iconPath={getXpIcon("/help")}
                testId={testIds.nipe.tourRelaunch}
                onClick={relaunchOrientation}
              />
              {/* F18 — customer-facing portal provisioning wizard. NIPE
                  can drive this surface via 5 onboarding tools but
                  never programmatically redirects (Round 1 hard limit). */}
              <MenuRow
                label="Set up SharePoint"
                iconPath={getXpIcon("/onboard/new")}
                testId="start-menu-onboard-new"
                onClick={() => {
                  onItemClick("/onboard/new");
                  onClose();
                }}
              />
              {/* Notes submenu — Obsidian-style tooling. Vault Graph
                  + Bases + Vault Flow are now panes inside Obsidian
                  Vault, not separate windows. */}
              {flags.obsidianVault && (
                <SubmenuRow
                  label="Notes"
                  iconPath={getXpIcon("/obsidian-vault")}
                  testId={testIds.startMenu.item("Notes")}
                  items={[
                    {
                      label: "Obsidian Import",
                      iconPath: getXpIcon("/obsidian-import"),
                      testId: testIds.startMenu.item("Obsidian Import"),
                      onClick: () => { onItemClick("/obsidian-import"); onClose(); },
                    },
                    {
                      label: "Obsidian Vault",
                      iconPath: getXpIcon("/obsidian-vault"),
                      testId: testIds.startMenu.item("Obsidian Vault"),
                      onClick: () => { onItemClick("/obsidian-vault"); onClose(); },
                    },
                  ]}
                />
              )}
            </div>

            {/* Right column — system tasks (Settings, Help, Profile, theme) */}
            <div
              className="py-1.5 pl-1"
              style={{
                background: "#d3e5fa",
                borderLeft: "1px solid #98c4f5",
              }}
            >
              <MenuRow
                label="User Profile"
                iconPath={getXpIcon("/profile")}
                testId={testIds.startMenu.item("User Profile")}
                onClick={() => {
                  onItemClick("/profile");
                  onClose();
                }}
              />
              <MenuRow
                label="Settings"
                iconPath={getXpIcon("/settings")}
                testId={testIds.startMenu.item("Settings")}
                onClick={() => {
                  onItemClick("/settings");
                  onClose();
                }}
              />
              {/* XP Search Companion → Corpus Search bridge. Click the row
                  to open with empty input; type into the inline field and
                  press Enter to open with the query pre-filled + auto-run. */}
              <StartMenuSearchRow
                onSubmit={(query) => {
                  onItemClick("/corpus-search");
                  if (query.trim().length > 0) {
                    const ev = new CustomEvent(START_MENU_SEARCH_EVENT, {
                      detail: { query: query.trim() },
                    });
                    // Re-fire with a tick of delay so CorpusSearchWindow
                    // has time to mount when this is the first open.
                    document.dispatchEvent(ev);
                    setTimeout(() => document.dispatchEvent(ev), 50);
                  }
                  onClose();
                }}
              />
              <MenuRow
                label="Help"
                iconPath={getXpIcon("/help")}
                testId={testIds.startMenu.item("Help")}
                onClick={() => {
                  onItemClick("/help");
                  onClose();
                }}
              />
            </div>
          </div>

          {/* Footer — Log Off / Shut Down (XP signature pair) */}
          <div
            className="flex justify-end gap-3 px-3 py-2"
            style={{
              background:
                "linear-gradient(180deg, var(--xp-taskbar-from) 0%, var(--xp-taskbar-to) 100%)",
              borderTop: "2px solid #245edb",
            }}
          >
            <button
              type="button"
              data-testid={testIds.startMenu.signOut}
              onClick={() => {
                onClose();
                void signOut();
              }}
              className="flex items-center gap-1.5 text-white cursor-pointer select-none"
              style={{
                fontFamily: "var(--xp-font-body)",
                fontSize: 12,
                background: "transparent",
                border: "none",
                textShadow: "1px 1px 1px rgba(0,0,0,0.4)",
                fontWeight: 700,
              }}
            >
              <img
                src={getXpIcon("/profile")}
                alt=""
                style={{ width: 18, height: 18, imageRendering: "pixelated" }}
              />
              Sign Out
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
